import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AppSidebar } from "@/components/app-sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";
import { UserPlus, Mail, Calendar, Trophy, Clock, CheckCircle, XCircle } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { 
  Invitation, 
  getInvitationsByInviter, 
  calculateInvitationPoints 
} from "@/services/invitationService";
import { InviteUserDialog } from "@/components/InviteUserDialog";
import { logger } from "@/lib/logger";

export default function InvitationsPage() {
  const { currentUser } = useAuth();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Redirect if not a Praktikant
  useEffect(() => {
    if (currentUser && currentUser.position !== 'praktikant') {
      toast({
        variant: "destructive",
        title: "Zugriff verweigert",
        description: "Diese Seite ist nur für Praktikanten verfügbar.",
      });
      // Redirect could be handled here, but we'll show an access denied message instead
    }
  }, [currentUser]);

  // Load invitations
  useEffect(() => {
    if (currentUser?.id && currentUser.position === 'praktikant') {
      loadInvitations();
    }
  }, [currentUser]);

  const loadInvitations = async () => {
    if (!currentUser?.id) return;
    
    try {
      setLoading(true);
      const userInvitations = await getInvitationsByInviter(currentUser.id);
      setInvitations(userInvitations);
      logger.log("Loaded invitations:", userInvitations);
    } catch (error) {
      logger.error("Error loading invitations:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Einladungen konnten nicht geladen werden.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInvitationCreated = () => {
    setDialogOpen(false);
    loadInvitations(); // Reload invitations
    toast({
      title: "Einladung gesendet",
      description: "Die Einladung wurde erfolgreich erstellt und wartet auf Admin-Genehmigung.",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'activated':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Ausstehend';
      case 'activated':
        return 'Aktiviert';
      case 'rejected':
        return 'Abgelehnt';
      default:
        return 'Unbekannt';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'activated':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPositionLabel = (position: string) => {
    const positionMap: { [key: string]: string } = {
      'praktikant': 'Praktikant',
      'dual-student': 'Dualer Student',
      'beraterassistent': 'Beraterassistent',
      'trainee': 'Trainee',
      'berater': 'Berater'
    };
    return positionMap[position] || position;
  };

  // Calculate total points earned and pending
  const totalPointsEarned = invitations
    .filter(inv => inv.status === 'activated' && inv.pointsAwarded)
    .reduce((sum, inv) => sum + inv.pointsAmount, 0);

  const totalPointsPending = invitations
    .filter(inv => inv.status === 'pending')
    .reduce((sum, inv) => sum + inv.pointsAmount, 0);

  if (currentUser?.position !== 'praktikant') {
    return (
      <div className="flex h-screen overflow-hidden bg-background">
        <AppSidebar />
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-center">Zugriff verweigert</CardTitle>
                <CardDescription className="text-center">
                  Diese Seite ist nur für Praktikanten verfügbar.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="py-6 px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Einladungen</h1>
              <p className="text-muted-foreground mt-2">
                Laden Sie neue Teammitglieder ein und verdienen Sie Punkte für erfolgreiche Aktivierungen
              </p>
            </div>
            <Button 
              onClick={() => setDialogOpen(true)}
              className="flex items-center gap-2"
            >
              <UserPlus size={20} />
              Neue Einladung
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Gesamt Einladungen</CardTitle>
                <Mail className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{invitations.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Aktiviert</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {invitations.filter(inv => inv.status === 'activated').length}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Punkte erhalten</CardTitle>
                <Trophy className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{totalPointsEarned}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Punkte ausstehend</CardTitle>
                <Clock className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{totalPointsPending}</div>
              </CardContent>
            </Card>
          </div>

          {/* Invitations Table */}
          <Card>
            <CardHeader>
              <CardTitle>Meine Einladungen</CardTitle>
              <CardDescription>
                Übersicht über alle von Ihnen gesendeten Einladungen
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="text-muted-foreground">Lade Einladungen...</div>
                </div>
              ) : invitations.length === 0 ? (
                <div className="text-center py-8">
                  <UserPlus className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Keine Einladungen</h3>
                  <p className="text-muted-foreground mb-4">
                    Sie haben noch keine Einladungen gesendet.
                  </p>
                  <Button onClick={() => setDialogOpen(true)}>
                    <UserPlus size={16} className="mr-2" />
                    Erste Einladung senden
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Position</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Punkte</TableHead>
                      <TableHead>Datum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invitations.map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell className="font-medium">
                          {invitation.inviteeName}
                        </TableCell>
                        <TableCell>
                          {invitation.inviteeEmail}
                        </TableCell>
                        <TableCell>
                          {getPositionLabel(invitation.inviteePosition)}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline"
                            className={getStatusColor(invitation.status)}
                          >
                            <div className="flex items-center gap-1">
                              {getStatusIcon(invitation.status)}
                              {getStatusText(invitation.status)}
                            </div>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Trophy size={14} className={
                              invitation.status === 'activated' && invitation.pointsAwarded
                                ? "text-yellow-500" 
                                : "text-muted-foreground"
                            } />
                            <span className={
                              invitation.status === 'activated' && invitation.pointsAwarded
                                ? "text-yellow-600 font-semibold" 
                                : "text-muted-foreground"
                            }>
                              {invitation.pointsAmount}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Calendar size={14} />
                            {format(invitation.createdAt, "dd.MM.yyyy", { locale: de })}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onInvitationCreated={handleInvitationCreated}
      />
    </div>
  );
}